import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Tooltip, { TooltipProps } from "@mui/joy/Tooltip";
import Link from "@mui/joy/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { GlossaryKey, GlossaryTerm, UE_GLOSSARY } from "../glossary";

/** Выше Sidebar (zIndex: 10000), как OverlayTooltip в system/inventorySync */
const TOOLTIP_Z_INDEX = 10001;

type TermHintProps = {
    /** Ключ из UE_GLOSSARY */
    term: GlossaryKey;
    /** Текст на экране; по умолчанию label из глоссария */
    children?: React.ReactNode;
    /** Размер иконки подсказки */
    iconSize?: number;
};

/**
 * Термин с тултипом: расшифровка + смысл + ссылка на ru.wikipedia (если есть).
 */
export function TermHint(props: TermHintProps) {
    const { term, children, iconSize = 14 } = props;
    const entry: GlossaryTerm = UE_GLOSSARY[term];

    const title = (
        <Box sx={{ maxWidth: 320, p: 0.25, display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Typography level="title-sm" sx={{ color: "common.white" }}>
                {entry.label}
            </Typography>
            <Typography level="body-xs" sx={{ color: "neutral.200" }}>
                {entry.expand}
            </Typography>
            <Typography level="body-sm" sx={{ color: "common.white" }}>
                {entry.meaning}
            </Typography>
            {entry.formula && (
                <Typography level="body-xs" sx={{ color: "neutral.200" }}>
                    Как считаем: {entry.formula}
                </Typography>
            )}
            {entry.example && (
                <Typography level="body-xs" sx={{ color: "neutral.200" }}>
                    Пример: {entry.example}
                </Typography>
            )}
            {entry.wikiRu && (
                <Link
                    href={entry.wikiRu}
                    target="_blank"
                    rel="noopener noreferrer"
                    level="body-xs"
                    sx={{ color: "primary.200", textDecoration: "underline" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    Открыть в Википедии (ru)
                </Link>
            )}
        </Box>
    );

    return (
        <Tooltip
            title={title}
            variant="solid"
            placement="top"
            disablePortal={false}
            slotProps={{
                root: {
                    sx: {
                        zIndex: TOOLTIP_Z_INDEX,
                        maxWidth: 360,
                    },
                },
            } as TooltipProps["slotProps"]}
        >
            <Box
                component="span"
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.35,
                    cursor: "help",
                    borderBottom: "1px dashed",
                    borderColor: "neutral.outlinedBorder",
                    verticalAlign: "baseline",
                }}
            >
                <Box component="span">{children ?? entry.label}</Box>
                <InfoOutlinedIcon sx={{ fontSize: iconSize, opacity: 0.65 }} />
            </Box>
        </Tooltip>
    );
}

/** Несколько терминов через разделитель, например ARPU / ARPPU */
export function TermHintsRow(props: {
    terms: GlossaryKey[];
    separator?: string;
}) {
    const { terms, separator = " / " } = props;
    return (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 0.25 }}>
            {terms.map((t, i) => (
                <React.Fragment key={t}>
                    {i > 0 && <Box component="span">{separator}</Box>}
                    <TermHint term={t} />
                </React.Fragment>
            ))}
        </Box>
    );
}
